/**
 * Cross-mode building blocks for 4-lane falling-tile rhythm modes
 * (Piano + Guitar). Both modes consume {@link ManiaNote} arrays and
 * run on the same engine in `engine/mania/`; per-mode tuning (timing
 * windows, perfect-zone fractions, render geometry) is supplied at
 * config-build time so the engine itself stays mode-agnostic.
 */

/**
 * Number of vertical lanes a 4K mania chart maps notes to. Hardcoded
 * to 4 — the standard 4K-mania convention. Changing this would
 * invalidate every authored chart and every piece of input/render
 * code that assumes a 4-wide grid, so it lives as a constant rather
 * than a per-chart setting.
 */
export const MANIA_LANE_COUNT = 4;

export type ManiaLane = 0 | 1 | 2 | 3;

/**
 * One playable tile in a 4-lane mania chart. Tiles fall down a lane
 * and the player taps the matching key (or lane button) when the tile
 * reaches the hit line.
 *
 * `dualPartnerLane` adds a chord accent: a second lane lights up at
 * the same beat. The engine expands a note's `dualPartnerLane` into
 * a second independent lane event so each half of the dual is judged
 * separately — a player who hits the primary but misses the partner
 * only loses HP for the missed half.
 *
 * `holdEndBeat` is set for long notes (the player holds the tile from
 * `beat` until `holdEndBeat`). Optional — short taps omit the field.
 * The engine judges holds in two phases: the head tap at `beat`
 * (geometric perfect-zone judging) and the release at `holdEndBeat`
 * (symmetric ms-window; late release within the late-grace window
 * auto-resolves as a lenient `good`).
 */
export interface ManiaNote {
  id: string;
  beat: number;
  lane: ManiaLane;
  /**
   * Companion lane for dual-tile accents. Must differ from `lane`.
   * The engine expands this into a second lane event at the same
   * beat; the two halves are judged independently so partial hits
   * cost only the missed half.
   */
  dualPartnerLane?: ManiaLane;
  /**
   * Beat at which a held note releases. Strictly greater than `beat`
   * when present. The engine judges this as a second scoring event
   * (release verdict) and tracks the lane as "held" between head and
   * release — subsequent `down`s on the same lane are no-ops while
   * the hold is engaged, so the player isn't penalised for the
   * finger still pressing the same key.
   */
  holdEndBeat?: number;
}

/**
 * Structural subset of a mania-shaped chart that the shared engine
 * consumes. Both `PianoChart` and `GuitarChart` satisfy this interface
 * (and add their own `mode` discriminator + chrome-specific fields).
 *
 * The engine reads only `bpm` and `totalBeats` from the chart at
 * runtime — note iteration is precomputed via {@link buildNoteLookup}
 * — so callers that build a config supply the per-mode tuning
 * (perfect-zone fraction, early-good fractions) explicitly via
 * {@link import('./engine/mania/core.js').ManiaEngineTuning} rather
 * than the engine reaching into mode-specific renderer constants.
 */
export interface ManiaChart {
  bpm: number;
  totalBeats: number;
  notes: ManiaNote[];
  approachMs: number;
  perfectWindowMs?: number;
  goodWindowMs?: number;
}

/**
 * Sort notes by beat ascending then id lexicographic. The engine reads
 * this canonical order so replay determinism doesn't depend on the
 * JSON producer's array order. Built once at config build time.
 */
export function buildNoteLookup(notes: ReadonlyArray<ManiaNote>): ManiaNote[] {
  return [...notes].sort((a, b) => {
    if (a.beat !== b.beat) {
      return a.beat - b.beat;
    }
    return a.id.localeCompare(b.id);
  });
}

/**
 * Tolerance used when comparing two beats for "same-beat" equality.
 * 1e-6 of a beat is well below any chart authoring precision and well
 * above any floating-point rounding the importer or onset synthesiser
 * could introduce.
 */
export const SAME_BEAT_EPSILON = 1e-6;

export function isManiaLane(value: unknown): value is ManiaLane {
  return value === 0 || value === 1 || value === 2 || value === 3;
}

/**
 * One issue produced by {@link validateManiaHoldOverlaps}. Mirror of
 * `ChartValidationIssue` from `chart.ts` — duplicated here to keep this
 * module dependency-free of the chart envelope types so it can be
 * called from any mania-shaped validator (Piano, Guitar, future modes).
 */
export interface ManiaValidationIssue {
  level: 'error' | 'warning';
  message: string;
  beat?: number;
}

/**
 * Reject hold notes whose [beat, holdEndBeat) interval overlaps a
 * later note in the same lane (or the dual-partner lane). The player
 * is physically holding the lane through the end-beat — a fresh tap
 * inside that window is unplayable and would silently auto-miss at
 * runtime.
 *
 * Per-mode same-beat collision policies (which differ between Piano
 * and Guitar — Piano forbids stacking primary notes; Guitar allows
 * lane-different stacking for chords/jacks) live in each chart
 * module's caller. This helper covers only the cross-cutting
 * hold-spans-a-tap case that's identical for both modes.
 *
 * Notes are expected pre-sorted by beat ascending. Issues are pushed
 * in-place onto the supplied array.
 */
export function validateManiaHoldOverlaps(
  sortedNotes: ReadonlyArray<ManiaNote>,
  issues: ManiaValidationIssue[],
): void {
  for (let i = 0; i < sortedNotes.length; i += 1) {
    const head = sortedNotes[i];
    if (head.holdEndBeat === undefined || !isManiaLane(head.lane)) {
      continue;
    }
    const heldLanes = new Set<ManiaLane>([head.lane]);
    if (head.dualPartnerLane !== undefined && isManiaLane(head.dualPartnerLane)) {
      heldLanes.add(head.dualPartnerLane);
    }
    for (let j = i + 1; j < sortedNotes.length; j += 1) {
      const next = sortedNotes[j];
      if (next.beat >= head.holdEndBeat) {
        break;
      }
      if (Math.abs(next.beat - head.beat) < SAME_BEAT_EPSILON) {
        continue;
      }
      if (!isManiaLane(next.lane)) {
        continue;
      }
      if (heldLanes.has(next.lane)) {
        issues.push({
          level: 'error',
          message: `Note ${next.id} at beat ${next.beat} lane ${next.lane} overlaps hold ${head.id} ending at beat ${head.holdEndBeat}`,
          beat: next.beat,
        });
      }
      if (
        next.dualPartnerLane !== undefined &&
        isManiaLane(next.dualPartnerLane) &&
        heldLanes.has(next.dualPartnerLane)
      ) {
        issues.push({
          level: 'error',
          message: `Note ${next.id} dualPartnerLane ${next.dualPartnerLane} overlaps hold ${head.id} ending at beat ${head.holdEndBeat}`,
          beat: next.beat,
        });
      }
    }
  }
}
